import React, { useEffect, useState } from "react";
import { Close } from "@mui/icons-material";
import { Box, IconButton, Typography, Stack } from "@mui/material";
import { keyframes } from "@mui/system";
import { API } from "../utils/Api";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const slideInUp = keyframes`
  from { transform: translateX(30px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const subtleGlow = keyframes`
  0% {
    box-shadow:
      0 8px 22px rgba(0, 0, 0, 0.25),
      0 0 0px rgba(255, 70, 70, 0.0);
  }
  50% {
    box-shadow:
      0 12px 42px rgba(255, 70, 70, 0.55),
      0 0 22px rgba(255, 70, 70, 0.85),
      0 0 45px rgba(255, 70, 70, 0.5);
  }
  100% {
    box-shadow:
      0 8px 22px rgba(0, 0, 0, 0.25),
      0 0 0px rgba(255, 70, 70, 0);
  }
`;

const pulseDot = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 6px rgba(255,80,80,0.6); }
  50% { transform: scale(1.35); box-shadow: 0 0 14px rgba(255,80,80,1); }
  100% { transform: scale(1); box-shadow: 0 0 6px rgba(255,80,80,0.6); }
`;

const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;
const CHECK_INTERVAL_MS = 10 * 60 * 1000;

const LAST_SHOWN_KEY = "criticalStockNotificationLastShown";

const Notification = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [outletData] = useState(
    JSON.parse(localStorage.getItem("outletData") || "null"),
  );

  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState("");

  const getRawMaterialsList = async () => {
    try {
      if (!token || !outletData?._id) return;

      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getOutletCurrentStock?outletId=${outletData._id}&limit=100`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const totalRecords = response?.data?.data?.totalRecords || [];

      // CRITICAL = below reorder level but > 0
      const criticalCount = totalRecords.filter((item) => {
        const reorder = item?.rawMaterial?.reOrderLevel;
        const stock = item?.currentStock;
        return reorder != null && stock > 0 && stock < reorder;
      }).length;

      // OUT OF STOCK = stock <= 0
      const outOfStockCount = totalRecords.filter(
        (item) => item?.currentStock <= 0,
      ).length;

      let msg = "";
      if (criticalCount > 0 && outOfStockCount > 0) {
        msg = `${criticalCount} raw materials are below reorder level and ${outOfStockCount} are out of stock.`;
      } else if (criticalCount > 0) {
        msg = `${criticalCount} raw materials are below reorder level.`;
      } else if (outOfStockCount > 0) {
        msg = `${outOfStockCount} raw materials are out of stock.`;
      }

      if (msg) {
        setMessage(msg);
        setVisible(true);
        localStorage.setItem(LAST_SHOWN_KEY, Date.now().toString());
      } else {
        setVisible(false);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    if (API?.BASE_URL === "https://xpanse.dev.bexcart.com/") {
      setVisible(false);
    }
  }, []);

  useEffect(() => {
    if (!token || !outletData?._id) return;

    const checkAndFetch = () => {
      const lastShown = localStorage.getItem(LAST_SHOWN_KEY);
      const now = Date.now();

      if (!lastShown || now - Number(lastShown) >= FOUR_HOURS_MS) {
        getRawMaterialsList();
      }
    };
    checkAndFetch();
    const intervalId = setInterval(checkAndFetch, CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [token, outletData?._id]);

  if (!visible) return null;

  const handleClick = () => {
    navigate("/criticalReport");
    setVisible(false);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setVisible(false);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        padding: "16px 20px",
        borderRadius: "18px",
        width: 340,
        cursor: "pointer",
        color: "#fff",
        zIndex: 3000,
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        gap: 0.75,
        background:
          "linear-gradient(145deg, rgba(0,18,32,0.92), rgba(0,64,100,0.95))",
        backdropFilter: "blur(14px)",
        border: "1px solid rgba(255,255,255,0.18)",
        animation: `${slideInUp} 0.45s ease-out, ${subtleGlow} 2.2s ease-in-out infinite`,
        transition: "all .25s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          border: "1px solid rgba(255,100,100,0.45)",
          boxShadow:
            "0 14px 50px rgba(255, 80, 80, 0.45), 0 0 25px rgba(255, 80, 80, 0.6)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: 11,
            height: 11,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #ff2e2e, #ff8a8a)",
            animation: `${pulseDot} 1.4s infinite ease-out`,
          }}
        />

        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.55,
            textTransform: "uppercase",
            fontSize: 11.5,
            color: "rgba(255,255,255,0.92)",
          }}
        >
          Critical Stock Alert
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: "#fff",
            p: 0.4,
            "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      <Typography
        variant="body2"
        sx={{
          opacity: 0.96,
          fontSize: 13.2,
          lineHeight: 1.45,
          color: "rgba(255,255,255,0.92)",
          mt: 0.5,
        }}
      >
        {message}
      </Typography>
    </Box>
  );
};

export default Notification;
